import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Users,
  Clock,
  BookOpen,
  MoreVertical,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CourseManager = ({ user }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, course: null });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/courses`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || data || []);
      } else {
        console.error('Failed to fetch courses:', response.status);
        // If unauthorized, redirect to login
        if (response.status === 401 || response.status === 403) {
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToggle = async (course) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/${course.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ published: !course.published })
      });
      
      if (response.ok) {
        setCourses(courses.map(c => 
          c.id === course.id ? { ...c, published: !c.published } : c
        ));
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.course) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/courses/${deleteDialog.course.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setCourses(courses.filter(c => c.id !== deleteDialog.course.id));
        setDeleteDialog({ open: false, course: null });
      }
    } catch (error) {
      console.error('Error deleting course:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          course.category?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                          (statusFilter === 'published' && course.published) ||
                          (statusFilter === 'draft' && !course.published);
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Course Manager</h1>
              <p className="text-gray-400">Create, edit, and manage your courses</p>
            </div>
          </div>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-500 mt-4 md:mt-0"
            onClick={() => navigate('/admin/courses/new')}
            data-testid="create-course-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Course
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-800 text-white"
              data-testid="search-courses-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-gray-900 border-gray-800 text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="all">All Courses</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Course Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{courses.length}</p>
                <p className="text-sm text-gray-500">Total Courses</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{courses.filter(c => c.published).length}</p>
                <p className="text-sm text-gray-500">Published</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{courses.filter(c => !c.published).length}</p>
                <p className="text-sm text-gray-500">Drafts</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {courses.reduce((acc, c) => acc + (c.total_students || 0), 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-500">Total Students</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses List */}
        {filteredCourses.length === 0 ? (
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No courses found</h3>
              <p className="text-gray-400 mb-6">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating your first course'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-500"
                  onClick={() => navigate('/admin/courses/new')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Course
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCourses.map((course) => (
              <Card 
                key={course.id} 
                className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-all"
                data-testid={`course-card-${course.slug}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Thumbnail */}
                    <div className="w-full md:w-48 h-28 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400'}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Course Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-white truncate">{course.title}</h3>
                            <Badge className={course.published 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                            }>
                              {course.published ? 'Published' : 'Draft'}
                            </Badge>
                            {course.featured && (
                              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                                Featured
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mb-2 line-clamp-2">{course.description}</p>
                        </div>
                      </div>
                      
                      {/* Meta Info */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          <span>{course.total_lessons || 0} lessons</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{(course.total_students || 0).toLocaleString()} students</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400" />
                          <span>{course.rating || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{course.duration || 'N/A'}</span>
                        </div>
                        <Badge variant="secondary" className="bg-gray-800 text-gray-300">
                          {course.category}
                        </Badge>
                        <span className="text-emerald-400 font-semibold">${course.price}</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex md:flex-col gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:bg-gray-800 flex-1 md:flex-none"
                        onClick={() => navigate(`/admin/courses/${course.id}/edit`)}
                        data-testid={`edit-course-${course.slug}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`border-gray-700 flex-1 md:flex-none ${
                          course.published 
                            ? 'text-yellow-400 hover:bg-yellow-500/10' 
                            : 'text-emerald-400 hover:bg-emerald-500/10'
                        }`}
                        onClick={() => handlePublishToggle(course)}
                        disabled={actionLoading}
                        data-testid={`toggle-publish-${course.slug}`}
                      >
                        {course.published ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Unpublish
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Publish
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-red-400 hover:bg-red-500/10 flex-1 md:flex-none"
                        onClick={() => setDeleteDialog({ open: true, course })}
                        data-testid={`delete-course-${course.slug}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete Course
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete "{deleteDialog.course?.title}"? This action cannot be undone.
              All enrollments and reviews for this course will also be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, course: null })}
              className="border-gray-700 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-delete-btn"
            >
              {actionLoading ? 'Deleting...' : 'Delete Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseManager;
